<template>
  <div>
    <menu-content-container
      :chip="$t('forms.rating_properties')"
      chip-secondary
      :border-bottom="false"
    >
    </menu-content-container>

    <menu-content-container border-full :padding-left-right="8">
      <div class="space-y-2">
        <QuestionForm />
        <CaptionsForm />
      </div>
    </menu-content-container>

    <menu-content-container :header="$t('forms.rating_segment_range')">
      <content-row style="display: block; justify-content: center">
        <TTabsV2
          data-testid="rating-mode-tabs"
          :defult-active-index="segmentMode === 'text' ? 0 : 1"
          :options="modeOptionsNavigation"
          @on-tab-change="onModeTabChanged"
        ></TTabsV2>
      </content-row>
      <content-row>
        <t-input-number
          v-model="countValue"
          data-testid="rating-segment-count"
          unit=""
          :min="2"
          :max="20"
          :label="$t('forms.rating_segment_count')"
        />
        <t-input-number
          v-model="startValue"
          data-testid="rating-segment-start"
          unit=""
          :label="$t('forms.rating_segment_start')"
        />
      </content-row>
      <content-row>
        <t-input-number
          v-model="segmentSize.model"
          data-testid="rating-segment-size"
          unit=""
          :label="$t('forms.rating_segment_size')"
          :min="16"
          :max="80"
          :changed="segmentSize.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="segmentSize.reset"
        />
        <t-input-number
          v-model="segmentGap.model"
          data-testid="rating-segment-gap"
          unit=""
          :label="$t('forms.rating_segment_gap')"
          :min="0"
          :max="20"
          :changed="segmentGap.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="segmentGap.reset"
        />
      </content-row>
      <content-row :margin-top="12">
        <t-switch
          v-model="autoWidth.model"
          :label="$t('forms.rating_segment_auto_width')"
          data-testid="rating-auto-width"
          :changed="autoWidth.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="autoWidth.reset"
        ></t-switch>
      </content-row>
    </menu-content-container>

    <SegmentStyleForm />

    <menu-content-container :header="$t('forms.rating_url_mode')" border-full>
      <content-row style="display: block; justify-content: center">
        <TTabsV2
          data-testid="url-mode-tabs"
          :defult-active-index="urlMode === 'pattern' ? 0 : 1"
          :options="urlOptionsNavigation"
          @on-tab-change="onUrlTabChanged"
        ></TTabsV2>
      </content-row>
      <content-row v-if="urlMode === 'pattern'" :margin-top="12">
        <t-input
          v-model="urlPattern"
          :label="$t('forms.rating_url_pattern')"
          placeholder="https://example.com/feedback?score={value}"
          data-testid="rating-url-pattern"
          @on-blur="onUrlPatternBlur"
        />
      </content-row>
      <content-row v-if="urlMode === 'pattern'" :margin-top="8">
        <t-note>{{ $t("forms.rating_url_pattern_hint") }}</t-note>
      </content-row>
    </menu-content-container>

    <menu-content-container :header="$t('forms.rating_settings')" border-full>
      <template #header-action>
        <t-input-toggle
          v-model="customizePerSegment"
          :label="$t('forms.rating_customize_per_segment')"
          data-testid="customize-per-segment"
        />
      </template>
      <transition name="slide-fade-borders" mode="out-in">
        <div v-if="customizePerSegment" key="per-segment-expanded">
          <t-note>{{ $t("forms.rating_settings_note") }}</t-note>
          <div class="mt-3 space-y-2">
            <SegmentValuesForm
              v-for="(_, i) in segmentList"
              :key="i"
              :i="i"
              :segment-mode="segmentMode"
              :url-mode="urlMode"
            />
          </div>
        </div>
        <div v-else key="per-segment-collapsed">
          <t-note>{{
            segmentMode === "text"
              ? $t("forms.rating_per_segment_text_hint")
              : $t("forms.rating_per_segment_image_hint")
          }}</t-note>
        </div>
      </transition>
    </menu-content-container>

    <paddingForm
      data-testid="rating-block-margin"
      :changed="padding.changed"
      :default-value="padding.defaultValue"
      @reset="padding.reset"
    />

    <menu-content-container :border-bottom="false">
      <content-row>
        <hide-on-mobile />
      </content-row>
      <content-row :margin-bottom="20" :margin-top="16">
        <hide-on-desktop />
      </content-row>
    </menu-content-container>
  </div>
</template>

<script lang="ts" setup>
  import paddingForm from "./paddingForm.vue";
  import hideOnMobile from "./components/hideOnMobile.vue";
  import hideOnDesktop from "./components/hideOnDesktop.vue";
  import QuestionForm from "./ratingBlock/questionForm.vue";
  import CaptionsForm from "./ratingBlock/captionsForm.vue";
  import SegmentStyleForm from "./ratingBlock/segmentStyleForm.vue";
  import SegmentValuesForm from "./ratingBlock/segmentValuesForm.vue";

  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useBlockAttr } from "@/composables/useBlockAttr";
  import { useI18n } from "vue-i18n";
  import { TopolRatingBlock } from "@/schemas/template/blocks/ratingBlockSchema";
  import { resolveSegmentHref } from "@/utils/substituteRatingUrl";
  import validHref from "@/utils/validHref";

  import {
    TInput,
    TInputNumber,
    TInputToggle,
    TNote,
    TSwitch,
    TTabsV2,
  } from "@topol/twig-ui";
  import { computed, ref, watch } from "vue";

  const mjmlStore = useMjmlStore();
  const { t } = useI18n();

  const segments = useBlockAttr({
    mode: "active-group",
    group: "segmentsAttributes",
  });
  const blockAttrs = useBlockAttr({ mode: "active" });

  const segmentSize = segments.px("size", 32);
  const segmentGap = segments.px("gap", 4);
  const autoWidth = segments.bool("autoWidth", true as any, false as any);
  const padding = blockAttrs.nullable("padding");

  const activeBlock = computed(
    () => mjmlStore.getActiveBlock as TopolRatingBlock | undefined
  );

  const segmentList = computed(() => {
    const block = activeBlock.value;
    if (!block?.children) return [];
    return block.children.filter(
      (c: any) => typeof c === "object" && typeof c.attributes === "object"
    );
  });

  const modeOptionsNavigation = computed(() => {
    interface ContentOptions {
      name: string;
      value: "text" | "image";
      disabled?: boolean;
      tooltip?: string;
    }

    const options: ContentOptions[] = [
      {
        name: t("forms.rating_segment_text"),
        value: "text",
        tooltip: "",
      },
      {
        name: t("forms.rating_segment_image"),
        value: "image",
        tooltip: "",
      },
    ];

    return options;
  });

  const urlOptionsNavigation = computed(() => {
    interface ContentOptions {
      name: string;
      value: "pattern" | "individual";
      disabled?: boolean;
      tooltip?: string;
    }

    const options: ContentOptions[] = [
      {
        name: t("forms.rating_url_mode_pattern"),
        value: "pattern",
        tooltip: "",
      },
      {
        name: t("forms.rating_url_mode_individual"),
        value: "individual",
        tooltip: "",
      },
    ];

    return options;
  });

  function onModeTabChanged(index: number) {
    segmentMode.value = modeOptionsNavigation.value[index].value;
  }

  function onUrlTabChanged(index: number) {
    const mode = urlOptionsNavigation.value[index].value;
    if (mode === urlMode.value) return;
    if (mode === "individual" && activeBlock.value) {
      const block = activeBlock.value;
      block.children.forEach((segment, i) => {
        if (!segment.attributes.href) {
          mjmlStore.updateBlockChildrenAttribute({
            childIndex: i,
            prop: "href",
            value: resolveSegmentHref(block, segment),
          });
        }
      });
    }
    updateGroup("url", "mode", mode);
  }

  function getGroup<T = any>(group: string, key: string, fallback: T): T {
    const block = activeBlock.value as any;
    if (!block) return fallback;
    const v = block[group]?.[key];
    return v === undefined || v === null ? fallback : v;
  }

  function updateGroup(group: string, key: string, value: any) {
    const block = activeBlock.value as any;
    if (!block) return;
    const current = (block[group] || {}) as Record<string, any>;
    mjmlStore.updateBlockProp({
      prop: group,
      value: { ...current, [key]: value },
    });
  }

  const segmentMode = computed({
    get() {
      return getGroup("segmentsAttributes", "mode", "text") as "text" | "image";
    },
    set(v: "text" | "image") {
      updateGroup("segmentsAttributes", "mode", v);
    },
  });

  const customizePerSegment = ref(segmentMode.value === "image");
  watch(segmentMode, (newMode) => {
    if (urlMode.value === "individual") return;
    if (newMode === "image") {
      customizePerSegment.value = true;
    } else {
      customizePerSegment.value = false;
    }
  });

  const startValue = computed({
    get() {
      return Number(getGroup("segmentsAttributes", "start", 0));
    },
    set(v: number) {
      resizeSegments(v, countValue.value);
    },
  });
  const countValue = computed({
    get() {
      return Number(getGroup("segmentsAttributes", "count", 11));
    },
    set(v: number) {
      resizeSegments(startValue.value, v);
    },
  });

  const urlPattern = computed({
    get() {
      return mjmlStore.getActiveBlockPropString("urlPattern") || "";
    },
    set(v: string) {
      mjmlStore.updateBlockProp({ prop: "urlPattern", value: v });
    },
  });

  function onUrlPatternBlur() {
    if (urlPattern.value && !validHref(urlPattern.value)) {
      urlPattern.value = "https://" + urlPattern.value;
    }
  }
  const urlMode = computed(() => getGroup("url", "mode", "pattern"));
  watch(urlMode, (newMode) => {
    if (newMode === "individual") {
      customizePerSegment.value = true;
    }
  });

  // Re-numbers segments to `start..start+count-1`: existing get new
  // value/label/alt, missing are added, extras removed. Custom labels are
  // overwritten — accepted trade-off for keeping values in sync with range.
  function resizeSegments(start: number, count: number) {
    const block = activeBlock.value;
    const sel = mjmlStore.selectedBlock;
    if (!block || !sel || count < 1) return;

    updateGroup("segmentsAttributes", "start", start);
    updateGroup("segmentsAttributes", "count", count);

    const pos = {
      sectionIndex: sel.sectionIndex,
      columnIndex: sel.columnIndex,
      blockIndex: sel.blockIndex,
    };
    const current = block.children.length;

    for (let i = 0; i < Math.min(count, current); i++) {
      const v = start + i;
      for (const [prop, val] of [
        ["value", v],
        ["label", String(v)],
        ["alt", String(v)],
      ] as const) {
        mjmlStore.updateBlockChildrenAttribute({
          childIndex: i,
          prop,
          value: val,
        });
      }
    }
    for (let i = current; i < count; i++) {
      const v = start + i;
      mjmlStore.addBlockChild({
        ...pos,
        childIndex: i,
        value: {
          tagName: "mj-rating-segment",
          attributes: {
            label: String(v),
            src: "",
            alt: String(v),
            href: "",
            value: v,
          },
        },
      });
    }
    for (let i = current - 1; i >= count; i--) {
      mjmlStore.deleteBlockChild({ ...pos, childIndex: i });
    }
  }
</script>
